import { Expose, Transform } from 'class-transformer';
import { DrupalTreeItem } from './drupal-menu-item';

export interface JsonApiData {
  type: string;
  id: string;
  attributes: any;
  relationships: any;
}

export interface TranslationItem {
  path: string;
  id: string;
}

export class DrupalIdentifier {
  @Expose()
  type!: string;

  @Expose()
  id!: string;
}

export class DrupalBase extends DrupalIdentifier {
  @Transform(({ obj }) => {
    if (obj?.attributes?.changed) {
      return obj?.attributes?.changed;
    }
    return undefined;
  })
  @Expose()
  changed?: string;

  @Transform(({ obj }) => {
    return obj?.attributes?.langcode;
  })
  @Expose()
  language!: string;

  @Transform(({ obj }) => {
    return obj?.attributes?.status;
  })
  @Expose()
  published!: boolean;
}

export class DrupalEntity extends DrupalBase {
  @Transform(({ obj }) => {
    return obj?.attributes?.title;
  })
  @Expose()
  title: string | undefined;

  @Transform(({ obj }) => {
    if (obj?.attributes?.summary) {
      return obj?.attributes?.summary;
    }
    return undefined;
  })
  @Expose()
  summary?: string;

  @Transform(({ obj }) => {
    const imageDerivatives =
      obj?.relationships?.thumbnail?.data?.meta?.fileRef?.data?.meta
        ?.imageDerivatives?.links;
    return imageDerivatives?.['original_512_webp']?.href;
  })
  @Expose()
  seoThumbnailUrl!: string | undefined;

  @Transform(({ obj }) => {
    const nodeLanguage = obj?.attributes?.langcode;
    const nodePath = obj?.attributes?.path;
    if (nodePath) {
      return `/${nodeLanguage}${nodePath}/`;
    }
    return `/${nodeLanguage}/`;
  })
  @Expose()
  path: string | undefined;

  @Transform(({ obj }) => {
    const rawTranslations = obj?.meta?.drupal?.translations;
    if (rawTranslations) {
      const translations: TranslationItem[] = [];
      Object.keys(rawTranslations).forEach((language) => {
        if (language !== obj?.attributes?.langcode) {
          const contentPath = rawTranslations[language].path;
          let path = `/${language}`;
          if (contentPath) {
            path = `${path}${contentPath}/`;
          } else {
            path = `${path}/`;
          }
          translations.push({
            id: language,
            path,
          });
        }
      });
      return translations;
    }
    return [];
  })
  @Expose()
  alternatives!: TranslationItem[];
}

export class DrupalTerm extends DrupalEntity implements DrupalTreeItem {
  @Transform(({ obj }) => {
    return obj?.attributes?.name;
  })
  @Expose()
  override title!: string;

  @Transform(({ obj }) => {
    return obj?.attributes?.description;
  })
  @Expose()
  description!: string;

  @Transform(({ obj }) => {
    return obj?.attributes?.weight;
  })
  @Expose()
  weight!: number;

  @Transform(({ obj }) => {
    if (obj?.relationships?.parent?.data?.length > 0) {
      const parentRef = obj?.relationships?.parent?.data[0];
      if (parentRef.id !== 'virtual') {
        return parentRef.id;
      }
      return undefined;
    }
    return undefined;
  })
  @Expose()
  parentId!: string;
}

export class DrupalNode extends DrupalEntity {
  @Transform(({ obj }) => {
    return obj?.attributes?.promote === true;
  })
  @Expose()
  promote?: boolean;

  @Transform(({ obj }) => {
    return obj?.attributes?.sticky === true;
  })
  @Expose()
  sticky?: boolean;

  /*@Transform(({ obj }) => {
    if (obj?.relationships?.thumbnail?.data) {
      const { reference, meta, ...rest } = obj.relationships.thumbnail.data;
      return rest;
    }
    return null;
  })
  @Expose()
  thumbnail?: ResourceIdentifier;*/

  @Transform(({ obj }) => {
    return obj?.relationships?.thumbnail?.data;
  })
  @Expose()
  thumbnail!: DrupalIdentifier | undefined;

  @Transform(({ obj }) => {
    const rawTranslations = obj?.meta?.drupal?.translations;
    const translations: Translations = {};
    if (rawTranslations) {
      Object.keys(rawTranslations).forEach((key) => {
        const rawTranslation: any = rawTranslations[key];
        const { id, isDefault, path, name } = rawTranslation;
        const translation: Translation = {
          language: id,
          isDefault,
          name,
          path: `/${id}${path}`,
        };
        translations[key] = translation;
      });
    }
    return translations;
  })
  @Expose()
  translations?: Translations;

  @Transform(({ obj }) => {
    if (obj?.attributes?.body?.processed) {
      return obj?.attributes?.body.processed;
    }
    if (obj?.attributes?.body) {
      return obj?.attributes?.body;
    }
    return undefined;
  })
  @Expose()
  description?: string;
}

export interface Translations {
  [language: string]: Translation;
}

export interface Translation {
  language: string;
  isDefault: boolean;
  name: string;
  path: string;
}
